<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { NButton, NEmpty, NSpin } from 'naive-ui'
import { useRepoCardHighlight } from '../composables/useRepoCardHighlight'
import { useIsMobile } from '../composables/useIsMobile'
import RouteShell from './RouteShell.vue'
import AgentAmberButton from './AgentAmberButton.vue'
import AgentRepoTable from './AgentRepoTable.vue'
import AgentRepoDetail from './AgentRepoDetail.vue'
import AgentRepoAddForm from './AgentRepoAddForm.vue'
import DispatchTaskModal from './DispatchTaskModal.vue'
import { useAgentReposStore } from '../stores/agent_repos'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin page to manage the cloud-agent repo registry. Lists repos from
// both the committed YAML config and DB-managed rows (added here) as a table
// (AgentRepoTable); the selected repo's devcontainer, checkout, tokens and
// product-owner settings open in the detail panel beside it (AgentRepoDetail).
// Tokens are write-only: only "set / not set" status is shown, never the token.
const store = useAgentReposStore()
const isMobile = useIsMobile()
useDocumentTitle('Agent repos')

// Rings the repo row named by a `?repo=` deep-link from AgentView's session
// context line (#1362) — and selects it, so its detail is what opens.
const { highlightedSlug, highlightFromQuery } = useRepoCardHighlight((slug) => store.repos.some((r) => r.slug === slug))

// Explicit pick, else the default repo, else the first one listed.
const selectedSlug = ref<string | null>(null)
const selectedRepo = computed(
  () =>
    store.repos.find((r) => r.slug === selectedSlug.value) ??
    store.repos.find((r) => r.is_default) ??
    store.repos[0] ??
    null,
)

// On a phone the panel stacks below the table, so bring it into view.
async function selectRepo(slug: string): Promise<void> {
  selectedSlug.value = slug
  if (!isMobile.value) return
  await nextTick()
  document.getElementById('agent-repo-detail')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
}

// The header's Add repository jumps to the add form below the table.
const addFormEl = ref<HTMLElement | null>(null)
function jumpToAddForm(): void {
  addFormEl.value?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  addFormEl.value?.querySelector('input')?.focus({ preventScroll: true })
}

// Same routine-dispatch modal the command palette opens.
const dispatchOpen = ref(false)

// Devcontainer status/metrics are polled rather than pushed live (#842) —
// an occasionally-checked ops page. 30s: the metrics behind this response
// are measured by a job on the agent box roughly once a minute, so polling
// faster only re-fetches numbers that haven't moved.
const STATUS_POLL_MS = 30_000
let pollTimer: ReturnType<typeof setInterval> | null = null

// Also wired to the Retry button next to the load error — without it the
// only recovery from a failed initial load is a full browser refresh.
async function loadRepos(): Promise<void> {
  try {
    await store.fetchAll()
  } catch {
    /* loadError surfaces in template */
  }
}

onMounted(async () => {
  await loadRepos()
  pollTimer = setInterval(() => {
    void store.fetchAll().catch(() => {})
  }, STATUS_POLL_MS)
  await highlightFromQuery()
  if (highlightedSlug.value) selectedSlug.value = highlightedSlug.value
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<template>
  <RouteShell>
    <div class="mx-auto flex max-w-[1240px] flex-wrap content-start gap-8 pb-6">
      <div class="flex min-w-0 flex-[999_1_520px] flex-col gap-5">
        <header class="flex flex-wrap items-end gap-3">
          <div class="flex flex-[1_1_260px] flex-col gap-1.5">
            <h1 class="page-title">Repos</h1>
            <p class="m-0 text-ink-muted">
              Where agents work. Add a repo here to store its GitHub PAT encrypted — no redeploy or SSH needed.
              YAML-backed repos (from <code class="font-mono text-[13px]">config/agent_repos.yml</code>) are read-only;
              a token saved here overrides their ENV-sourced credentials.
            </p>
            <p class="m-0 text-sm text-ink-faint" data-testid="agent-repos-latest-cli-version">
              Latest Claude Code CLI available:
              <span class="font-mono text-ink-muted">{{ store.latestCliVersion ?? 'Unknown' }}</span>
            </p>
          </div>
          <button
            type="button"
            class="min-h-10 rounded-[10px] border border-border-control px-4 font-medium text-ink hover:bg-surface-hover"
            data-testid="agent-repos-add-jump"
            @click="jumpToAddForm"
          >
            Add repository
          </button>
          <AgentAmberButton class="min-h-10 px-4" data-testid="agent-repos-dispatch" @click="dispatchOpen = true">
            Dispatch task
          </AgentAmberButton>
        </header>

        <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
          <NSpin />
        </div>

        <div
          v-else-if="store.loadError"
          class="flex items-center gap-3 text-sm text-status-alert"
          data-testid="agent-repos-load-error"
        >
          <span>Failed to load repos: {{ store.loadError }}</span>
          <NButton
            size="small"
            secondary
            :loading="store.loading"
            data-testid="agent-repos-load-retry"
            @click="loadRepos"
          >
            Retry
          </NButton>
        </div>

        <NEmpty v-else-if="store.repos.length === 0" description="No repos configured." class="my-12" />

        <AgentRepoTable
          v-else
          :repos="store.repos"
          :selected-slug="selectedRepo?.slug ?? null"
          :highlighted-slug="highlightedSlug"
          @select="selectRepo"
        />
        <p class="m-0 text-[13px] text-ink-faint">
          Pick a repo to see its devcontainer, checkout, tokens and product owner. The row's ··· menu holds metrics
          refresh, default, workspace override and removal.
        </p>

        <div ref="addFormEl" class="scroll-mt-4">
          <AgentRepoAddForm />
        </div>
      </div>

      <AgentRepoDetail
        v-if="selectedRepo && !store.loadError"
        :key="selectedRepo.slug"
        class="min-w-0 flex-[1_1_300px] lg:max-w-[380px]"
        :repo="selectedRepo"
        :all-repos="store.repos"
        :latest-cli-version="store.latestCliVersion"
      />
    </div>

    <DispatchTaskModal v-model:show="dispatchOpen" />
  </RouteShell>
</template>
